import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { searchByQuery } from "../features/search/search.thunk";

type TSearchSuccessResponse = {
  q: string;
  status: "single_product" | "multiple_products" | "no_products";
};

type TSearchFailedResponse = {
  q: string;
};

type TUseSearchParams = {
  initialValue?: string | null;
  onSuccess?: (res: TSearchSuccessResponse) => void;
  onError?: (res: TSearchFailedResponse) => void;
};

export const useSearch = (params: TUseSearchParams = {}) => {
  const [searchValue, setSearchValue] = useState(params?.initialValue ?? "");

  const { error, isLoading } = useAppSelector((state) => state.search);

  const dispatch = useAppDispatch();

  const isValidASIN = (str: string) => {
    const asinRegex = /^(B0)?[0-9A-Z]{8,10}$/;
    return asinRegex.test(str.trim());
  }

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      const q = searchTerm.trim();

      if (!q) return;

      if (isValidASIN(q)) {
        return params?.onSuccess?.({ q, status: "single_product" });
      }

      try {
        const res = await dispatch(searchByQuery({ q })).unwrap();
        if (
          res.length === 1 &&
          (res[0].asin === q || res[0].upc === q || res[0].isbn === q)
        ) {
          params?.onSuccess?.({ q: res[0].asin, status: "single_product" });
        } else if (res.length !== 0) {
          params?.onSuccess?.({ q, status: "multiple_products" });
        } else {
          params?.onSuccess?.({ q, status: "no_products" });
        }
      } catch {
        params?.onError?.({ q });
      }
    },
    [dispatch, params],
  );

  return {
    searchValue,
    setSearchValue,
    handleSearch,
    error,
    isLoading,
  };
};
